import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaFacebookF, FaTwitter, FaLinkedinIn, FaInstagram, FaEnvelope, FaPhone, FaMapMarkerAlt } from 'react-icons/fa';
import * as authService from '../services/authService';

const Footer = () => {
  const [currentUser, setCurrentUser] = useState(authService.getCurrentUser());
  const [isLoggedIn, setIsLoggedIn] = useState(authService.isLoggedIn());

  useEffect(() => {
    const checkLoginStatus = () => {
      const user = authService.getCurrentUser();

      if (user) {
        setIsLoggedIn(true);
        setCurrentUser(user);
      } else {
        setIsLoggedIn(false);
        setCurrentUser(null);
      }
    };

    checkLoginStatus();
    window.addEventListener('userLogin', checkLoginStatus);
    window.addEventListener('userLogout', checkLoginStatus);

    return () => {
      window.removeEventListener('userLogin', checkLoginStatus);
      window.removeEventListener('userLogout', checkLoginStatus);
    };
  }, []);

  // Scroll to top when clicking any link
  const handleLinkClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (document.body) {
      document.body.scrollTo({ top: 0, behavior: 'smooth' });
      document.body.scrollTop = 0;
    }
    if (document.documentElement) {
      document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
      document.documentElement.scrollTop = 0;
    }
  };





  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-y-12 gap-x-8 sm:gap-x-12">
          {/* About Section - Full width on mobile, 1 column on desktop */}
          <div className="col-span-2 lg:col-span-1">
            <div className="mb-6">
              <img src="/logo.png" alt="Paper Portal" className="h-16 mb-6" />
            </div>
            <p className="text-gray-400 mb-8 leading-relaxed max-w-sm text-base sm:text-lg">
              Your trusted platform for academic publishing. Submit, manage, and track your research papers with ease.
            </p>
            <div className="flex space-x-6">
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center text-gray-400 hover:bg-primary-blue hover:text-white transition-all transform hover:-translate-y-1">
                <FaFacebookF size={20} />
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center text-gray-400 hover:bg-sky-500 hover:text-white transition-all transform hover:-translate-y-1">
                <FaTwitter size={20} />
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center text-gray-400 hover:bg-blue-600 hover:text-white transition-all transform hover:-translate-y-1">
                <FaLinkedinIn size={20} />
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center text-gray-400 hover:bg-pink-600 hover:text-white transition-all transform hover:-translate-y-1">
                <FaInstagram size={20} />
              </a>
            </div>
          </div>

          {/* Resources Section - 1 column on mobile (half width), 1 column on desktop */}
          <div className="col-span-1">
            <h3 className="text-white font-bold text-xl mb-8 relative inline-block">
              Resources
              <span className="absolute -bottom-2 left-0 w-12 h-1 bg-primary-blue rounded-full"></span>
            </h3>
            <ul className="space-y-4">
              <li>
                <Link to="/author-guidelines" onClick={handleLinkClick} className="text-gray-400 hover:text-white transition-colors text-base sm:text-lg hover:translate-x-1 inline-block transform duration-200">
                  Author Guidelines
                </Link>
              </li>
              <li>
                <Link to="/reviewer-guidelines" onClick={handleLinkClick} className="text-gray-400 hover:text-white transition-colors text-base sm:text-lg hover:translate-x-1 inline-block transform duration-200">
                  Reviewer Guidelines
                </Link>
              </li>
              <li>
                <Link to="/publication-ethics" onClick={handleLinkClick} className="text-gray-400 hover:text-white transition-colors text-base sm:text-lg hover:translate-x-1 inline-block transform duration-200">
                  Publication Ethics
                </Link>
              </li>
              <li>
                <Link to="/faqs" onClick={handleLinkClick} className="text-gray-400 hover:text-white transition-colors text-base sm:text-lg hover:translate-x-1 inline-block transform duration-200">
                  FAQs
                </Link>
              </li>
              <li>
                <Link to="/privacy-policy" onClick={handleLinkClick} className="text-gray-400 hover:text-white transition-colors text-base sm:text-lg hover:translate-x-1 inline-block transform duration-200">
                  Privacy Policy
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Section - 1 column on mobile (half width), 1 column on desktop */}
          <div className="col-span-1">
            <h3 className="text-white font-bold text-xl mb-8 relative inline-block">
              Contact Us
              <span className="absolute -bottom-2 left-0 w-12 h-1 bg-primary-blue rounded-full"></span>
            </h3>
            <ul className="space-y-6">
              <li className="flex items-start gap-4">
                <div className="mt-1 w-10 h-10 rounded-lg bg-primary-blue/10 flex items-center justify-center shrink-0">
                  <FaMapMarkerAlt className="text-primary-blue text-lg" />
                </div>
                <span className="text-gray-400 text-base sm:text-lg leading-snug">
                  Paper Portal, Room 601, Primarc Tower, Sector-5, Kolkata 700123
                </span>
              </li>
              <li className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary-blue/10 flex items-center justify-center shrink-0">
                  <FaPhone className="text-primary-blue text-lg" />
                </div>
                <span className="text-gray-400 text-base sm:text-lg">+91 7890204000</span>
              </li>
              <li className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary-blue/10 flex items-center justify-center shrink-0">
                  <FaEnvelope className="text-primary-blue text-lg" />
                </div>
                <span className="text-gray-400 text-base sm:text-lg break-all">info@paperportal.com</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6 md:gap-0">
            <p className="text-gray-400 text-base sm:text-lg text-center md:text-left">
              © {new Date().getFullYear()} Paper Portal / A Product of Riz Software Consultancy.
            </p>
            <div className="flex flex-wrap justify-center md:justify-end gap-x-6 gap-y-3">
              <Link to="/terms-and-conditions" onClick={handleLinkClick} className="text-gray-400 hover:text-white text-base sm:text-lg transition-colors whitespace-nowrap">
                Terms & Conditions
              </Link>
              <Link to="/privacy-policy" onClick={handleLinkClick} className="text-gray-400 hover:text-white text-base sm:text-lg transition-colors whitespace-nowrap">
                Privacy Policy
              </Link>
              <Link to="/refund-policy" onClick={handleLinkClick} className="text-gray-400 hover:text-white text-base sm:text-lg transition-colors whitespace-nowrap">
                Refund Policy
              </Link>
              <Link to="/shipping-policy" onClick={handleLinkClick} className="text-gray-400 hover:text-white text-base sm:text-lg transition-colors whitespace-nowrap">
                Shipping Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;